'use client';

import React from 'react';
import { observer } from '@legendapp/state/react';
import type { SelectedGiftCartProps } from '../available-cart-level-gifts.types';
import styles from '../available-cart-level-gifts.module.css';
import { Text } from '../../../../../utils';
import { Card, cn, PromotionalItemCard } from '@kidslabs/mario-nextjs-react-ui-core';
import { useCartGiftSelection } from '../../../hooks/use-cart-gift-selection.hook';
import type { SelectedGift } from '../../../types/gift-selection.types';

/**
 * Map a store SelectedGift to display props
 */
const getGiftDisplayKey = (gift: SelectedGift): string => `${gift.ruleId}-${gift.item?.sku ?? gift.productId}`;

const getGiftImageUrl = (gift: SelectedGift): string => gift.item?.small_image?.url ?? gift.item?.image?.url ?? '';

/**
 * SelectedGiftCart Component
 *
 * Displays cart-level gifts that have been selected by the user.
 *
 * REACTIVE ARCHITECTURE:
 * - Uses Legend-State observer() to subscribe to the gift selection store
 * - cartId is obtained from useCart() inside useCartGiftSelection hook
 * - Any change to store$.selectedGifts automatically re-renders this component
 * - No manual refetch triggers, lazy queries, or local state needed
 */
export const SelectedGiftCart: React.FC<SelectedGiftCartProps> = observer(
    ({
        testId: _testId,
        onGiftPress,
        onRemoveGift: _onRemoveGift,
        showActionButton,
        isMobile,
        className,
        titleClassName,
        contentClassName,
    }) => {
        // Reactively subscribe to selected gifts from Legend-State store
        // cartId is resolved internally by useCartGiftSelection via useCart()
        const { selectedGifts, ruleName } = useCartGiftSelection({ createNewSession: false });

        if (selectedGifts.length === 0) {
            return null;
        }

        return (
            <Card className={cn(styles.selectedGiftCart, className)}>
                <div className={cn(styles.cardTitle, titleClassName)}>
                    <Text className={styles.cardTitleText}>{ruleName}</Text>
                </div>

                <div className={cn(styles.cardContent, contentClassName)}>
                    {selectedGifts.map(gift => (
                        <div key={getGiftDisplayKey(gift)} className={styles.giftListContainer}>
                            <PromotionalItemCard
                                mode="detailed"
                                title={gift.item?.name ?? ''}
                                imageSource={getGiftImageUrl(gift)}
                                quantity={gift.quantity ?? 1}
                                priceText="Free"
                                actionButtonText="Đổi quà"
                                showActionButton={showActionButton}
                                onActionPress={() => onGiftPress(gift.ruleId ?? 0)}
                                innerContainerClassName={styles.innerContainerClassName}
                                imageContainerClassName={styles.imageContainer}
                                titleWrapperClassName={isMobile ? styles.titleRowMobile : styles.titleRow}
                                quantityClassName={isMobile ? styles.quantityMobile : styles.quantity}
                                contentClassName={isMobile ? styles.contentMobile : styles.content}
                            />
                        </div>
                    ))}
                </div>
            </Card>
        );
    }
);

export default SelectedGiftCart;
